import React, { useState, useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import withLayout from "../../hocs/Layout";
import { Button, Divider, Grid, Skeleton, Typography, Box } from "@mui/material";
import { FormProvider, RHFDateRangePickerField, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import InitiatedNdrData from "./InitiatedNdr";
import ClosedNdrData from "./ClosedNdr";
import PendingNdrData from "./PendingNdr";
import { DoneAllOutlined, PauseCircle, PublishedWithChangesOutlined, UploadFileOutlined, WhatsApp, PhoneInTalk, CheckCircleOutline, CancelOutlined } from "@mui/icons-material";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import MUIModal from "../../common/MUIModal";
import { buildFilterConditions, formatNumber, maskAddress, maskPhoneNumber, parseDateAndTime, shouldMaskData, formatDate, formatDateForApi } from "../../helpers/UtilityHelper";
import { useGetNDRDataQuery } from "../../redux/commonRTK";
import NDRBulkUpload from "./BulkUploads";
import { getMerchant, getToken } from "../../helpers/ReduxHelpers";
import { getGeneric } from "../../common/constants";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { subDays } from "date-fns";
import PageTitle from "../../common/PageTitle";
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, ERROR, WARNING, withOpacity } from '../../styles/foundation/colors';
import { pageContainerSx, filterBarSx, primaryBtnSx, secondaryBtnSx } from '../../styles/patterns';
import { primaryTextSx, secondaryTextSx, cellStackSx } from '../orders/list2/cellStyles';

const NDRListing = () => {

  const [filterChange, setFiltersChange] = useState(0);
  const [filters, setFilters] = useState(0);
  const [active, setActive] = useState(0);
  const filterConditions = buildFilterConditions(filters);

  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''
  const [interventionConfig, setInterventionConfig] = useState(null);

  useEffect(() => {
    if (!merchantId) return;
    getGeneric('intervention/rules/config', { merchantOid: merchantId, type: 'NDR' })
      .then((res) => { setInterventionConfig(res?.data) })
      .catch(() => { setInterventionConfig(null) })
  }, [merchantId]);

  const [dateRange, setDateRange] = useState([{
    endDate: new Date(),
    startDate: subDays(new Date(), 15),
    key: 'selection'
  }])

  const { data: pendingData, isLoading: pendingLoading } = useGetNDRDataQuery({
    andfilter: [
      {
        field: "orderStatus",
        operator: "in",
        value: ["FAILED_DELIVERY", "SHIPMENT_HELD"],
      },
      {
        field: "ndrData.ndrStatus",
        operator: "nin",
        value: ["INITIATED", "CLOSED"],
      },
      { field: "merchant.$id", operator: "eq", value: merchantId },
      ...filterConditions.andfilter
    ],
    orfilter: filterConditions.orfilter,
    offset: 0,
    limit: 1,
    includes: "id",
    countOnly: true,
  })

  const { data: initiatedData, isLoading: initiatedLoading } = useGetNDRDataQuery({
    andfilter: [
      {
        field: 'ndrData.ndrStatus',
        operator: 'eq',
        value: 'INITIATED',
      },
      { field: "orderStatus", operator: "nin", value: ["LOST", "DISPOSED_OFF"] },
      { field: "merchant.$id", operator: "eq", value: merchantId },
      ...filterConditions.andfilter
    ],
    orfilter: filterConditions.orfilter,
    offset: 0,
    limit: 1,
    includes: "id",
    // countOnly short-circuits the backend right after countDocuments — no find, so no
    // blocking sort on the unindexed awbRegisteredDate, which was timing out this call
    // and leaving the tab badge at 0 while the list below rendered rows.
    countOnly: true,
  });

  // Closed NDR count — same filter as the Closed tab (ClosedNdr.js), so the tab badge
  // and summary show the real closed count instead of a blank.
  const { data: closedData, isLoading: closedLoading } = useGetNDRDataQuery({
    andfilter: [
      { field: 'ndrData.ndrStatus', operator: 'eq', value: 'CLOSED' },
      { field: "merchant.$id", operator: "eq", value: merchantId },
      ...filterConditions.andfilter
    ],
    orfilter: filterConditions.orfilter,
    offset: 0,
    limit: 1,
    includes: "id",
    countOnly: true,
  });

  // ── This-month NDR summary ──────────────────────────────────────────────
  // Date-bounded counts (awbRegisteredDate >= 1st of the current month) — the same
  // date field the page's own Created-Date filter uses. Being month-bounded also keeps
  // these countOnly calls fast (no unbounded scan), so the Closed number resolves here.
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const monthLabel = formatDate(monthStart, 'MMMM yyyy');
  const monthDateFilter = { field: 'awbRegisteredDate', operator: 'gte', value: formatDateForApi(monthStart, 'start') };
  const monthCountQuery = (statusFilters) => ({
    andfilter: [...statusFilters, { field: 'merchant.$id', operator: 'eq', value: merchantId }, monthDateFilter],
    offset: 0, limit: 1, includes: 'id', countOnly: true,
  });
  const { data: mPending } = useGetNDRDataQuery(monthCountQuery([
    { field: 'orderStatus', operator: 'in', value: ['FAILED_DELIVERY', 'SHIPMENT_HELD'] },
    { field: 'ndrData.ndrStatus', operator: 'nin', value: ['INITIATED', 'CLOSED'] },
  ]));
  const { data: mInitiated } = useGetNDRDataQuery(monthCountQuery([
    { field: 'ndrData.ndrStatus', operator: 'eq', value: 'INITIATED' },
    { field: 'orderStatus', operator: 'nin', value: ['LOST', 'DISPOSED_OFF'] },
  ]));
  const { data: mClosed } = useGetNDRDataQuery(monthCountQuery([
    { field: 'ndrData.ndrStatus', operator: 'eq', value: 'CLOSED' },
  ]));
  const monthlySummary = [
    { label: 'Pending', value: mPending?.count, color: ERROR },
    { label: 'Initiated', value: mInitiated?.count, color: WARNING },
    { label: 'Closed', value: mClosed?.count, color: SUCCESS },
  ];

  const methods = useForm();
  const {
    handleSubmit,
    reset,
  } = methods;

  const onSubmit = (data) => {
    setFiltersChange(filterChange + 1)
    setFilters(data);
  }

  const pendingCount = pendingData?.count || 0;
  const initiatedCount = initiatedData?.count || 0;
  const closedCount = closedData?.count || 0;

  const tabs = [
    { label: 'Pending', icon: <PauseCircle />, count: pendingCount, loading: pendingLoading, color: ERROR },
    { label: 'Initiated', icon: <PublishedWithChangesOutlined />, count: initiatedCount, loading: initiatedLoading, color: WARNING },
    { label: 'Closed', icon: <DoneAllOutlined />, count: closedCount, loading: closedLoading, color: SUCCESS },
    { label: 'Bulk Actions', icon: <UploadFileOutlined />, count: null, color: PRIMARY_BLUE },
  ];

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle="Manage non-delivery reports — reattempt, update address, authorize RTO, and track resolution">NDR Dashboard</PageTitle>

      {/* This-month NDR summary (pending / initiated / closed) */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
          NDR Summary · {monthLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {monthlySummary.map((s) => (
            <Box key={s.label} sx={{ flex: '1 1 160px', minWidth: 150, p: 1.5, bgcolor: WHITE, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${s.color}`, borderRadius: '10px' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 10, fontWeight: WEIGHT.medium, color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: WEIGHT.extra, color: s.color, lineHeight: 1.2 }}>{(s.value ?? 0).toLocaleString()}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Intervention config status */}
      {interventionConfig && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px',
          }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: interventionConfig.whatsappAutomatedNdr ? SUCCESS + '14' : ERROR + '14',
              color: interventionConfig.whatsappAutomatedNdr ? SUCCESS : ERROR,
            }}>
              <WhatsApp sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.medium }}>
              WhatsApp NDR
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {interventionConfig.whatsappAutomatedNdr
                ? <CheckCircleOutline sx={{ fontSize: 14, color: SUCCESS }} />
                : <CancelOutlined sx={{ fontSize: 14, color: ERROR }} />}
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: interventionConfig.whatsappAutomatedNdr ? SUCCESS : ERROR }}>
                {interventionConfig.whatsappAutomatedNdr ? 'Enabled' : 'Disabled'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
            bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px',
          }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS + '14' : ERROR + '14',
              color: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS : ERROR,
            }}>
              <PhoneInTalk sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, fontWeight: WEIGHT.medium }}>
              NDR Calling
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              {interventionConfig.futworksNdrConfig?.pushLead
                ? <CheckCircleOutline sx={{ fontSize: 14, color: SUCCESS }} />
                : <CancelOutlined sx={{ fontSize: 14, color: ERROR }} />}
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: interventionConfig.futworksNdrConfig?.pushLead ? SUCCESS : ERROR }}>
                {interventionConfig.futworksNdrConfig?.pushLead ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Status tabs */}
      <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex' }}>
          {tabs.map((t, i) => {
            const isActive = active === i;
            const countDisplay = t.count != null ? (t.count > 99999 ? '99,999+' : t.count?.toLocaleString?.() ?? '—') : null;
            return (
              <React.Fragment key={t.label}>
                {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />}
                <Box
                  onClick={() => { setActive(i); setFiltersChange(filterChange + 1); }}
                  sx={{
                    flex: '1 1 auto', minWidth: 130, py: 1.25, px: 2, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    borderBottom: isActive ? `3px solid ${t.color}` : '3px solid transparent',
                    bgcolor: isActive ? withOpacity(t.color, 0.06) : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: withOpacity(t.color, 0.04) },
                  }}
                >
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: '8px',
                    bgcolor: isActive ? withOpacity(t.color, 0.12) : withOpacity(t.color, 0.07),
                    color: isActive ? t.color : SUBTLE,
                    '& .MuiSvgIcon-root': { fontSize: 17 },
                  }}>
                    {t.icon}
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium,
                      color: isActive ? NAVY : SUBTLE, lineHeight: 1.2,
                    }}>
                      {t.label}
                    </Typography>
                    {t.loading ? (
                      <Skeleton variant="text" width={28} height={18} sx={{ mt: 0.2 }} />
                    ) : countDisplay != null && (
                      <Typography sx={{
                        fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold,
                        color: isActive ? t.color : NAVY, lineHeight: 1.3,
                      }}>
                        {countDisplay}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Filter bar for Pending/Initiated/Closed tabs */}
      {[0, 1, 2].includes(active) && (
        <Box data-rp-filters sx={filterBarSx}>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <RHFTextField name="reference" label="Order Number" size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <RHFTextField name="awb" label="AWB Number" size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <RHFTextField name="city" label="City" size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <RHFTextField name="state" label="State" size="small" />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <RHFDateRangePickerField name="created_date" label="Created Date" maxDate={new Date()} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="small" sx={primaryBtnSx}>Filter</Button>
                  <Button variant="outlined" size="small" sx={secondaryBtnSx} onClick={() => {
                    reset({ state: '', city: '', awb: '', customerName: '', reference: '', date: '' })
                    setFiltersChange(filterChange + 1);
                    setFilters(0);
                  }}>Reset</Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      )}

      {/* Tab content */}
      {active === 0 && <PendingNdrData filters={filters} filterChange={filterChange} />}
      {active === 1 && <InitiatedNdrData filters={filters} filterChange={filterChange} />}
      {active === 2 && <ClosedNdrData filters={filters} filterChange={filterChange} />}
      {active === 3 && <NDRBulkUpload />}
    </Box>
  );
};
export function ActionLogModal(props) {
  const { report } = useSelector(state => state);
  const { piiAccessAllow } = jwt_decode(getToken());
  const { data, logModal, setLogModal } = props;
  const actionLogModal = [
    {
      field: "ndrAction",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Action",
      flex: 0.6,
      minWidth: 100,
    },
    {
      field: "reattemptNumber",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Reattempt No.",
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: "updatedAddress",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Updated Address",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box sx={cellStackSx}>
            <Typography sx={secondaryTextSx}>
              {shouldMaskData(report, row, piiAccessAllow) ? maskAddress(
                row?.updatedAddress
              ) : (
                row?.updatedAddress
              )}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "updatedContactNumber",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Updated Contact Number",
      flex: 0.6,
      minWidth: 160,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box sx={cellStackSx}>
            <Typography sx={primaryTextSx}>
              {shouldMaskData(report, row, piiAccessAllow) ? maskPhoneNumber(
                row?.updatedContactNumber
              ) : (
                row?.updatedContactNumber
              )}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "cpResponse",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Action Status",
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: "actionTakenBy",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Action Taken By",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "reattemptRequestDate",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Reattempt Request Date",
      flex: 0.8,
      minWidth: 160,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Typography sx={secondaryTextSx}>
            {parseDateAndTime(row?.ndrCurrentAttemptDate, true)}
          </Typography>
        )
      },
    },
    {
      field: "actionDate",
      headerAlign: "left",
      hideSortIcons: true,
      headerName: "Action Date",
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => {
        const { row } = params;
        return (
          <Typography sx={secondaryTextSx}>
            {parseDateAndTime(row?.actionDate)}
          </Typography>
        )
      },
    },

  ];
  return (<MUIModal
    open={logModal}
    title="NDR Action Log"
    handleClose={() => setLogModal(false)}
  >
    <ProzoDataGrid
      autoHeight={true}
      columns={actionLogModal}
      rows={data ? data : []}
      paginationMode="client"
      rowCount={data?.length}
      getRowId={(row) => row.actionDate}
    />
  </MUIModal>
  )
}

export { NDRListing }
export default withLayout(NDRListing);
